import { create } from "zustand"

import type {
  GlAccount,
  GlAccountClassification,
  GlAccountSortField,
  GlAccountType,
  ListGlAccountsQuery,
  ReportingAccountType,
  SortingOrder,
} from "@/graphql/codegen/graphql"
import { ListGlAccountsDocument } from "@/graphql/codegen/graphql"
import { type SearchFilters, createSearchStore } from "@/stores/createSearchStore"

export type GlAccountSearchFilters = SearchFilters & {
  field?: GlAccountSortField
  order?: SortingOrder
  classification?: GlAccountClassification
  accountTypes?: GlAccountType[]
  reportingAccountType?: ReportingAccountType
  showInactive?: boolean
}

const deduplicateData = (newData: GlAccount[], existingData: GlAccount[]): GlAccount[] => {
  const uniqueIds = new Set(existingData.map((account) => account.glAccountId))
  return [...existingData, ...newData.filter((account) => account.glAccountId && !uniqueIds.has(account.glAccountId))]
}

export const useChartOfAccountsStore = create(
  createSearchStore<GlAccount, GlAccountSearchFilters, ListGlAccountsQuery>({
    documentNode: ListGlAccountsDocument,
    deduplicateData,
    getQueryInput: (filters: GlAccountSearchFilters, offset: number, limit: number) => {
      const { field, order, classification, accountTypes, reportingAccountType, showInactive, query } = filters
      return {
        offset,
        limit,
        filter: {
          query,
          classification,
          type: accountTypes,
          reportingAccountType,
          isActive: showInactive === false ? true : undefined, // Only filter active if showInactive is false
        },
        sort: field && order ? { field, order } : undefined,
      }
    },
    getQueryVariables: (queryInput) => ({ input: queryInput }),
    extractDataFromResponse: (data) => ({
      __typename: data.glAccount.list.__typename,
      data:
        data?.glAccount.list.__typename === "GlAccountListSuccess"
          ? (data.glAccount.list.glAccounts.filter((account) => account !== null) as GlAccount[])
          : [],
      error: data?.glAccount.list.__typename === "GlAccountFailure" ? data.glAccount.list.error : undefined,
    }),
  })
)
